import { useState, useEffect } from "react";
import { Map, MapMarker } from "react-kakao-maps-sdk";
import { isAppWebView } from "utils/api";

// 카카오맵 공식 URL Scheme (apis.map.kakao.com URL Scheme 문서).
// 같은 액션 경로를 App / MobileWeb 두 형태로 제공한다.
//   App       : kakaomap://search?q=...&p=lat,lng
//   MobileWeb : https://m.map.kakao.com/scheme/search?q=...&p=lat,lng
// 앱이 설치돼 있으면 App 스킴이 뜨고, 아니면 MobileWeb 스킴이 같은 기능을 대신한다.
const KAKAO_APP_SCHEME = "kakaomap://";
const KAKAO_MOBILE_WEB_SCHEME = "https://m.map.kakao.com/scheme/";
// 커스텀 스킴을 쓸 수 없는 환경(데스크탑, 스킴을 못 여는 WebView)용 카카오맵 링크 API.
// 마커 + 라벨이 찍힌 웹 지도로 연결된다.
const KAKAO_WEB_LINK = "https://map.kakao.com/link";

const isMobileDevice = () =>
    typeof navigator !== "undefined" &&
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

// 안드로이드 WebView 여부.
// MobileWeb 스킴은 applink.map.kakao.com 브리지 페이지를 거쳐 kakaomap:// 으로
// 리다이렉트하는데, WebView 는 앱이 shouldOverrideUrlLoading 에서 받아주지 않는 한
// 커스텀 스킴을 열지 못하고 ERR_UNKNOWN_URL_SCHEME 으로 실패한다. (iOS 는 정상 동작)
const isAndroidWebView = () =>
    typeof window !== "undefined" && Boolean(window.Android);

// 앱이 "외부 앱/브라우저로 열기" 브리지를 제공하는 경우에만 true.
// 브리지가 있으면 App 스킴을 네이티브가 직접 처리할 수 있다.
const hasNativeExternalOpen = () =>
    isAndroidWebView() && typeof window.Android?.openExternalUrl === "function";

/**
 * 네이티브 브리지로 URL 열기를 위임한다.
 * @param {string} url
 * @returns {boolean} 네이티브가 받아갔으면 true
 */
const openViaNativeBridge = (url) => {
    try {
        window.Android.openExternalUrl(url);
        return true;
    } catch (error) {
        console.warn("네이티브 외부 열기 브리지 호출 실패:", error);
        return false;
    }
};

/**
 * 공식 URL Scheme 의 액션 경로(쿼리 포함)를 만든다.
 * App 스킴과 MobileWeb 스킴이 이 경로를 그대로 공유한다.
 * 좌표가 있으면 좌표 기준으로, 없으면 장소명/주소로 검색한다.
 *
 * @param {{name?: string, address?: string, lat?: number, lng?: number}} place
 * @returns {string|null} 넘길 위치 정보가 없으면 null
 */
const buildKakaoSchemePath = ({ name, address, lat, lng }) => {
    const query = (name || address || "").trim();
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

    // 좌표 근처에서 장소명으로 검색 → 이름이 붙은 마커가 표시된다.
    if (hasCoords && query) {
        return `search?q=${encodeURIComponent(query)}&p=${lat},${lng}`;
    }
    if (hasCoords) return `look?p=${lat},${lng}`;
    if (query) return `search?q=${encodeURIComponent(query)}`;
    return null;
};

/**
 * 커스텀 스킴 없이 열 수 있는 카카오맵 웹 지도 URL.
 *
 * @param {{name?: string, address?: string, lat?: number, lng?: number}} place
 * @returns {string|null} 넘길 위치 정보가 없으면 null
 */
const buildKakaoWebUrl = ({ name, address, lat, lng }) => {
    const query = (name || address || "").trim();
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

    if (hasCoords) {
        return `${KAKAO_WEB_LINK}/map/${encodeURIComponent(
            query || "선택한 위치"
        )},${lat},${lng}`;
    }
    if (query) return `${KAKAO_WEB_LINK}/search/${encodeURIComponent(query)}`;
    return null;
};

/**
 *
 * @param {*} info
 * @param {string} info.name 장소명
 * @param {string} [info.address] 주소
 * @param {number} info.lat 위도
 * @param {number} info.lng 경도
 * @returns
 */
const LocationMap = ({ info, hasOpenAppBtn = false }) => {
    // 카카오맵 API 안전하게 접근
    const kakao = window?.kakao;

    const [markers, setMarkers] = useState([]);
    // 카카오맵 앱/웹으로 넘길 확정된 위치 정보 (검색으로 좌표를 찾은 경우 포함)
    const [resolvedPlace, setResolvedPlace] = useState(null);
    const [map, setMap] = useState();
    const [isLoading, setIsLoading] = useState(true);
    // 장소명 검색 결과가 없으면 지도·지도에서 보기 버튼을 숨긴다.
    const [isNotFound, setIsNotFound] = useState(false);

    // 기본 좌표 (서울 시청)
    const defaultCenter = {
        lat: 37.5665,
        lng: 126.978,
    };

    // location 상태를 안전하게 초기화
    const [location, setLocation] = useState(() => {
        if (info && info.lat && info.lng) {
            return {
                lat: parseFloat(info.lat),
                lng: parseFloat(info.lng),
            };
        }
        return defaultCenter;
    });

    useEffect(() => {
        if (!map || !kakao) {
            setIsLoading(false);
            return;
        }

        setIsNotFound(false);

        var imageSrc =
            "https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png";
        var imageSize = new kakao.maps.Size(24, 35);
        var markerImage = new kakao.maps.MarkerImage(imageSrc, imageSize);

        // 이미 좌표가 있는 경우
        if (info && info.lat && info.lng) {
            const position = {
                lat: parseFloat(info.lat),
                lng: parseFloat(info.lng),
            };

            setMarkers([
                {
                    position,
                    name: info.name,
                    image: markerImage,
                },
            ]);
            setLocation(position);
            setResolvedPlace({
                name: info.name ?? "",
                address: info.address ?? "",
                lat: position.lat,
                lng: position.lng,
            });
            setIsLoading(false);
            return;
        }

        // 장소명으로 검색하는 경우
        if (!info?.name) {
            setIsLoading(false);

            return;
        }

        try {
            const ps = new kakao.maps.services.Places();

            ps.keywordSearch(info.name, (data, status, _pagination) => {
                setIsLoading(false);

                if (
                    status === kakao.maps.services.Status.OK &&
                    data.length > 0
                ) {
                    const bounds = new kakao.maps.LatLngBounds();
                    const newMarkers = [];

                    // 첫 번째 검색 결과만 사용

                    const place = data[0];
                    const position = {
                        lat: parseFloat(place.y),
                        lng: parseFloat(place.x),
                    };

                    newMarkers.push({
                        position,
                        name: place.place_name,
                        image: markerImage,
                    });

                    bounds.extend(new kakao.maps.LatLng(place.y, place.x));

                    setMarkers(newMarkers);
                    setLocation(position);
                    setResolvedPlace({
                        name: place.place_name || info.name || "",
                        address:
                            place.road_address_name || place.address_name || "",
                        lat: position.lat,
                        lng: position.lng,
                    });
                    map.setBounds(bounds);
                } else {
                    // 검색 결과가 없는 경우
                    console.warn("장소 검색 결과가 없습니다:", info.name);
                    setIsNotFound(true);
                }
            });
        } catch (error) {
            setIsLoading(false);
            setIsNotFound(true);

            console.error("카카오맵 검색 중 오류:", error);
        }
    }, [map, kakao, info]);

    const handleOpenMap = () => {
        // 검색으로 좌표를 찾았으면 그 결과를, 아직이면 넘겨받은 info를 그대로 사용
        const target = resolvedPlace ?? {
            name: info?.name ?? "",
            address: info?.address ?? "",
            lat: info?.lat ? parseFloat(info.lat) : null,
            lng: info?.lng ? parseFloat(info.lng) : null,
        };

        const schemePath = buildKakaoSchemePath(target);

        // 안드로이드 WebView + 외부 열기 브리지: 문서의 App 스킴을 네이티브에 위임한다.
        if (schemePath && hasNativeExternalOpen()) {
            if (openViaNativeBridge(`${KAKAO_APP_SCHEME}${schemePath}`)) return;
        }

        // 그 외 앱(WebView)·모바일 웹은 MobileWeb 스킴으로.
        // 단 브리지 없는 안드로이드 WebView 는 커스텀 스킴을 못 열어
        // ERR_UNKNOWN_URL_SCHEME 이 뜨므로 스킴을 타지 않는 웹 지도로 떨어뜨린다.
        const useMobileWebScheme =
            (isAppWebView() || isMobileDevice()) && !isAndroidWebView();

        const url =
            schemePath && useMobileWebScheme
                ? `${KAKAO_MOBILE_WEB_SCHEME}${schemePath}`
                : buildKakaoWebUrl(target);

        if (!url) {
            console.warn("카카오맵으로 전달할 위치 정보가 없습니다.");
            return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
    };

    if (isNotFound) return null;

    // 카카오맵 API가 로드되지 않은 경우
    if (!kakao) {
        return (
            <div className="page-location-detail">
                <div
                    style={{
                        width: "100%",
                        height: "160px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        border: "1px solid #ddd",
                        borderRadius: "4px",
                    }}
                >
                    <p style={{ color: "#666", margin: 0 }}>
                        카카오맵을 불러오는 중입니다...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-location-detail">
            {isLoading && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "280px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        zIndex: 1000,
                    }}
                >
                    <p style={{ color: "#666", margin: 0 }}>
                        지도를 불러오는 중...
                    </p>
                </div>
            )}

            {location !== null &&            
            <>
                <Map
                    center={location}
                    style={{
                        width: "100%",
                        height: "280px",
                    }}
                    // draggable 이 false 면 휠·핀치 줌까지 함께 막히므로 켜 둔다.
                    draggable
                    zoomable
                    scrollwheel
                    onCreate={setMap}
                >
                    {markers.map((marker) => (
                        <MapMarker
                            key={`marker-${marker.content}-${marker.position.lat},${marker.position.lng}`}
                            position={marker.position}
                        >
                        </MapMarker>
                    ))}
                </Map>
                {hasOpenAppBtn && 
                <div className="map-view-btn-wrap">
                  <button className="btn btn-outline-gray200 btn-medium"  onClick={handleOpenMap}>지도에서 보기</button>
                </div>}
            </>
          }
        </div>
    );
};

export default LocationMap;
